import { HelpOutline, AccessTime, Archive, Done, Warning, Error } from "@mui/icons-material";
import { Chip, Tooltip, Typography, Box } from "@mui/material";
import {
  Datagrid,
  DateField,
  FunctionField,
  NumberField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  useGetRecordId,
  useGetOne,
  List,
} from "react-admin";
import { format } from "date-fns";
import { IALoading } from "../components/IAProgress";

export const DocumentShow = () => {
  const id = useGetRecordId();
  const { data: record, isLoading, error } = useGetOne("documents", { id });
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", margin: "5em" }}>
        Loading Document Data...
        <IALoading />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ textAlign: "center", margin: "5em" }}>
        <Typography variant="h6" color="error">
          Error loading document: {error.message}
        </Typography>
      </Box>
    );
  }
  if (!record) {
    return (
      <Box sx={{ textAlign: "center", margin: "5em" }}>
        <Typography variant="h6">Document not found</Typography>
      </Box>
    );
  }
  const getEnhancementVersion = (versionID) => {
    switch (versionID) {
      case null:
        return "App Did Not Support Mobile Image Enhancement.";
      case 2:
        return "App Supported Mobile Image Enhancement.";
      case 3:
        return "App Supported Mobile/Server Image Enhancement.";
      default:
        return versionID;
    }
  };
  return (
    <Show>
      <TabbedShowLayout syncWithLocation={false} spacing={2}>
        <Tab label="Document Information" path="info">
          <TextField source="document_uuid" />
          <TextField source="driver_email" label="Sender's Email" />
          <TextField source="file_format" />
          <NumberField source="num_pages" label="Number of pages" />
          <TextField source="version_scanned_on" />
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.75em" }}>
            Enhancement Version:{" "}
          </Typography>
          <Typography variant="body2" style={{ marginTop: "-1vh" }}>
            {getEnhancementVersion(record.image_enhancement_version)}
          </Typography>
          <TextField source="image_enhancement_type" />
          <DateField
            showTime
            source="created_at"
            label={
              <span>
                Created at{" "}
                <Tooltip title="Mobile app first contacted Axle about uploading. If possible, the phone starts uploading speculatively before the driver even taps send in the app. This timestamp can be before the Driver Sent At time.">
                  <HelpOutline style={{ width: "1.3vh" }} />
                </Tooltip>
              </span>
            }
          />
          <DateField
            showTime
            source="updated_at"
            label={
              <span>
                Updated at{" "}
                <Tooltip title="Document was last updated at">
                  <HelpOutline style={{ width: "1.3vh" }} />
                </Tooltip>
              </span>
            }
          />
          <DateField
            showTime
            source="upload_finished_at"
            label={
              <span>
                Upload finished at{" "}
                <Tooltip title="Mobile app finished uploading">
                  <HelpOutline style={{ width: "1.3vh" }} />
                </Tooltip>
              </span>
            }
          />
        </Tab>
        <Tab label="Document Shares" path="shares">
          <DocumentShareShow record={record} />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const StatusShareChip = ({ share }) => {
  const chipStyles = {
    fontWeight: 600,
    borderRadius: "8px",
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" && share.document_status === "Uploaded" ? "#166534" : undefined,
    color: (theme) => (theme.palette.mode === "dark" ? "#fff" : undefined),
  };

  const getStatusStyles = () => {
    if (share.hidden_at) {
      return share.document_status === "REJECTED"
        ? { label: "Rescan Requested", color: "warning", icon: <Warning /> }
        : { label: "Deleted", color: "error", icon: <Error /> };
    }
    const statusMap = {
      Initialized: { label: "Initialized", color: "default", icon: <AccessTime /> },
      Uploaded: { label: "Uploaded", color: "success", icon: <Done /> },
      Archived: { label: "Archived", color: "info", icon: <Archive /> },
      "In Review": { label: "In Review", color: "warning", icon: <AccessTime /> },
      Rejected: { label: "Rejected", color: "error", icon: <Error /> },
    };
    return (
      statusMap[share.document_status] || {
        label: share.document_status || "Unknown",
        color: "error",
        icon: null,
      }
    );
  };

  const { label, color, icon } = getStatusStyles();
  return <Chip label={label} color={color} icon={icon} size="small" sx={chipStyles} />;
};

export const DocumentShareShow = ({ record }) => {
  if (!record || !Array.isArray(record.document_shares)) {
    return (
      <Box sx={{ textAlign: "center", margin: "35px" }}>
        <Typography variant="h6">No shares available</Typography>
      </Box>
    );
  }

  return (
    <List pagination={false} exporter={false}>
      <Datagrid data={record.document_shares} size="medium" bulkActionButtons={false} rowClick={false}>
        <TextField source="customer_type" emptyText="Recipient" />
        <TextField source="recipient_email" emptyText="-" />
        <TextField source="bill_of_lading_number" label="BOL #" emptyText="-" />
        <FunctionField
          label="Driver Sent At"
          source="driver_sent_at"
          render={(share) => (share.driver_sent_at ? format(new Date(share.driver_sent_at), "PPp") : "-")}
        />
        <FunctionField
          label="Integration File Generated At"
          source="integration_file_generated_at"
          render={(share) =>
            share.integration_file_generated_at ? format(new Date(share.integration_file_generated_at), "PPp") : "-"
          }
        />
        <FunctionField
          label="Downloaded At"
          source="downloaded_via_ftp_at"
          render={(share) => (share.downloaded_via_ftp_at ? format(new Date(share.downloaded_via_ftp_at), "PPp") : "-")}
        />
        <FunctionField
          label="Status"
          source="document_status"
          render={(share) => (
            <StatusShareChip
              share={{
                document_status: share.document_status,
                hidden_at: share.hidden_at,
              }}
            />
          )}
        />
      </Datagrid>
    </List>
  );
};
